import { useCallback, useState } from 'react';
import { Icon } from '@/shared/components/ui/Icon';
import { useI18n } from '@/shared/i18n/useT';

export type HomeView = 'list' | 'cards';

const STORAGE_KEY = 'numtable_home_view';

type Views = Readonly<Record<string, HomeView>>;

function readViews(): Views {
  try {
    const raw: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}');
    // Before views were per tab this key held one bare value; it is dropped.
    return raw && typeof raw === 'object' ? (raw as Views) : {};
  } catch {
    return {};
  }
}

/** The list/cards choice for one tab, remembered per browser. */
export function useHomeView(tab: string): [HomeView, (view: HomeView) => void] {
  const [views, setViews] = useState<Views>(readViews);
  const update = useCallback(
    (next: HomeView) => {
      setViews((current) => {
        const merged = { ...current, [tab]: next };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch { /* per-browser nicety only */ }
        return merged;
      });
    },
    [tab],
  );
  return [views[tab] === 'cards' ? 'cards' : 'list', update];
}

interface Props {
  value: HomeView;
  onChange: (view: HomeView) => void;
}

/** Segmented list/cards toggle; the selected half is tinted. */
export function ViewSwitch({ value, onChange }: Props) {
  const { t } = useI18n();
  const options: { view: HomeView; icon: 'menu' | 'grid'; label: string }[] = [
    { view: 'list', icon: 'menu', label: t('home.view.list') },
    { view: 'cards', icon: 'grid', label: t('home.view.cards') },
  ];

  return (
    <div
      role="radiogroup"
      aria-label={t('home.view.label')}
      className="flex h-9 overflow-hidden rounded-full border border-border-strong"
    >
      {options.map(({ view, icon, label }, index) => {
        const selected = value === view;
        return (
          <button
            key={view}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={label}
            title={label}
            onClick={() => onChange(view)}
            className={[
              'flex items-center px-3 text-fg',
              index > 0 ? 'border-l border-border-strong' : '',
              selected ? 'bg-accent-soft' : 'hover:bg-surface-3',
            ].join(' ')}
          >
            <Icon name={icon} className="h-4 w-4" />
          </button>
        );
      })}
    </div>
  );
}
